import { useCallback, useMemo, useRef, useState } from 'react';

import { Carriers } from '../../../../../common/constants/carriers';
import { LetterTypes, Sources } from '../generator/types';

import { isSingleSelectLetterType } from './blockResolver';
import { blockValuePrefix } from './fieldKeys';
import {
  LetterDrawerContextValue,
  OpenLetterDrawerArgs,
  PricingMode,
} from './types';

interface InternalState {
  letterType: LetterTypes | null;
  isExpanded: boolean;
  isJuvenile: boolean;
  carrier: Carriers | null;
  carrierDisplayName: string;
  policyNumber: string;
  recipientFullName: string;
  proposedInsuredName: string;
  riders: string[];
  selectedSources: Sources[];
  pricingMode: PricingMode;
  fcraEnabled: boolean;
  selectedRiders: string[];
  fieldValues: Record<string, string>;
  onUpload: ((file: File[]) => void) | null;
  pendingLetterType: LetterTypes | null;
}

function sameStrArr(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function sameFieldValues(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (a[k] !== b[k]) return false;
  return true;
}

// Dirty = current user-editable state differs from the snapshot taken at open()
// (or the most recent letter-type switch). Avoids false positives when the
// drawer pre-seeds selectedRiders from args.riders, since the snapshot includes
// that same pre-seed.
function computeIsDirty(s: InternalState, snap: InternalState): boolean {
  if (!sameStrArr(s.selectedSources, snap.selectedSources)) return true;
  if (!sameStrArr(s.selectedRiders, snap.selectedRiders)) return true;
  if (s.fcraEnabled !== snap.fcraEnabled) return true;
  if (!sameFieldValues(s.fieldValues, snap.fieldValues)) return true;
  return false;
}

const initialState: InternalState = {
  letterType: null,
  isExpanded: false,
  isJuvenile: false,
  carrier: null,
  carrierDisplayName: '',
  policyNumber: '',
  recipientFullName: '',
  proposedInsuredName: '',
  riders: [],
  selectedSources: [],
  pricingMode: 'higher',
  fcraEnabled: false,
  selectedRiders: [],
  fieldValues: {},
  onUpload: null,
  pendingLetterType: null,
};

// Drops a block's typed values and its paragraph/contact/removed-field
// overrides. Typed values are stored per block, so this never touches another
// block that happens to use the same field name.
function withoutBlockData(
  fieldValues: Record<string, string>,
  id: Sources,
): Record<string, string> {
  const next = { ...fieldValues };
  const prefixes = [
    blockValuePrefix(id),
    `__para_${id}_`,
    `__contact_${id}_`,
    `__field_removed_${id}_`,
  ];
  for (const key of Object.keys(next)) {
    if (prefixes.some(p => key.startsWith(p))) delete next[key];
  }
  return next;
}

export interface UseLetterDrawerReturn extends LetterDrawerContextValue {
  open: (args: OpenLetterDrawerArgs) => void;
  uploadFiles: (files: File[]) => void;
}

export function useLetterDrawer(): UseLetterDrawerReturn {
  const [state, setState] = useState<InternalState>(initialState);
  const initialSnapshot = useRef<InternalState>(initialState);

  const open = useCallback((args: OpenLetterDrawerArgs) => {
    const next: InternalState = {
      ...initialState,
      letterType: args.letterType,
      isExpanded: true,
      isJuvenile: args.isJuvenile,
      carrier: args.carrier,
      carrierDisplayName: args.carrierDisplayName,
      policyNumber: args.policyNumber,
      recipientFullName: args.recipientFullName,
      proposedInsuredName: args.proposedInsuredName,
      riders: args.riders,
      // Mirror legacy OTAF modal: pre-select all ineligible riders from the case.
      // {{riders}} resolves on first paint instead of showing placeholder.
      selectedRiders: args.riders,
      onUpload: args.onUpload,
    };
    setState(next);
    initialSnapshot.current = next;
  }, []);

  const close = useCallback(() => {
    setState(initialState);
    initialSnapshot.current = initialState;
  }, []);

  const toggleExpanded = useCallback(() => {
    setState(s => ({ ...s, isExpanded: !s.isExpanded }));
  }, []);

  const toggleSource = useCallback((id: Sources) => {
    setState(s => {
      const isSelected = s.selectedSources.includes(id);
      if (!isSelected) {
        if (
          isSingleSelectLetterType(s.letterType) &&
          s.selectedSources.length > 0
        ) {
          // One reason per letter: picking another replaces the current one.
          let fieldValues = s.fieldValues;
          for (const prevId of s.selectedSources) {
            fieldValues = withoutBlockData(fieldValues, prevId);
          }
          return { ...s, selectedSources: [id], fieldValues };
        }
        return { ...s, selectedSources: [...s.selectedSources, id] };
      }
      // Removing a card: clear its block-scoped data so re-adding starts fresh.
      return {
        ...s,
        selectedSources: s.selectedSources.filter(x => x !== id),
        fieldValues: withoutBlockData(s.fieldValues, id),
      };
    });
  }, []);

  const reorderSources = useCallback((fromIndex: number, toIndex: number) => {
    setState(s => {
      if (fromIndex === toIndex) return s;
      const next = [...s.selectedSources];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);

      // applyInAdditionPrefix (letterBodyBuilder.ts) skips the "In addition,"
      // prefix only for the block at index 0, so only the block leaving
      // position 0 and the block arriving at position 0 can change whether
      // their first paragraph should show it. Their cached freeform-edit
      // override (BlockCard's paraKey/paraHtmlKey) was captured under the old
      // position, so it must be cleared here or it keeps getting patched
      // in-place with the stale prefix/casing forever (RP-8305) — including
      // in the PDF/copy output, which reads paraKey directly.
      const prevFirst = s.selectedSources[0];
      const nextFirst = next[0];
      let fieldValues = s.fieldValues;
      if (prevFirst !== nextFirst) {
        fieldValues = { ...s.fieldValues };
        for (const id of [prevFirst, nextFirst]) {
          if (id === undefined) continue;
          delete fieldValues[`__para_${id}_0`];
          delete fieldValues[`__para_${id}_0_html`];
        }
      }

      return { ...s, selectedSources: next, fieldValues };
    });
  }, []);

  const setPricingMode = useCallback((mode: PricingMode) => {
    setState(s => ({ ...s, pricingMode: mode, selectedSources: [] }));
  }, []);

  const setFcraEnabled = useCallback((v: boolean) => {
    setState(s => ({ ...s, fcraEnabled: v }));
  }, []);

  const toggleRider = useCallback((rider: string) => {
    setState(s => ({
      ...s,
      selectedRiders: s.selectedRiders.includes(rider)
        ? s.selectedRiders.filter(r => r !== rider)
        : [...s.selectedRiders, rider],
    }));
  }, []);

  const setFieldValue = useCallback((key: string, value: string) => {
    setState(s => ({ ...s, fieldValues: { ...s.fieldValues, [key]: value } }));
  }, []);

  const isDirty = useMemo<boolean>(
    () => computeIsDirty(state, initialSnapshot.current),
    [state],
  );

  const requestLetterType = useCallback(
    (next: LetterTypes) => {
      if (state.letterType === next) return;
      if (isDirty) {
        setState(s => ({ ...s, pendingLetterType: next }));
      } else {
        // No dirty state — just open the new letter type, preserving case-level data
        setState(s => {
          const switched: InternalState = {
            ...initialState,
            letterType: next,
            isExpanded: true,
            isJuvenile: s.isJuvenile,
            carrier: s.carrier,
            carrierDisplayName: s.carrierDisplayName,
            policyNumber: s.policyNumber,
            recipientFullName: s.recipientFullName,
            proposedInsuredName: s.proposedInsuredName,
            riders: s.riders,
            selectedRiders: s.riders,
            onUpload: s.onUpload,
          };
          // Re-baseline the snapshot to the post-switch state so dirty checks
          // measure changes made AFTER the switch, not before.
          initialSnapshot.current = switched;
          return switched;
        });
      }
    },
    [state.letterType, isDirty],
  );

  const confirmLetterTypeSwitch = useCallback(() => {
    setState(s => {
      const target = s.pendingLetterType;
      if (!target) return s;
      const switched: InternalState = {
        ...initialState,
        letterType: target,
        isExpanded: true,
        isJuvenile: s.isJuvenile,
        carrier: s.carrier,
        carrierDisplayName: s.carrierDisplayName,
        policyNumber: s.policyNumber,
        recipientFullName: s.recipientFullName,
        proposedInsuredName: s.proposedInsuredName,
        riders: s.riders,
        selectedRiders: s.riders,
        onUpload: s.onUpload,
      };
      // Re-baseline the snapshot to the post-switch state.
      initialSnapshot.current = switched;
      return switched;
    });
  }, []);

  const cancelLetterTypeSwitch = useCallback(() => {
    setState(s => ({ ...s, pendingLetterType: null }));
  }, []);

  const uploadFiles = useCallback(
    (files: File[]) => {
      if (state.onUpload) {
        state.onUpload(files);
      }
    },
    [state.onUpload],
  );

  return useMemo<UseLetterDrawerReturn>(
    () => ({
      letterType: state.letterType,
      isOpen: state.letterType !== null,
      isExpanded: state.isExpanded,
      isJuvenile: state.isJuvenile,
      carrier: state.carrier,
      carrierDisplayName: state.carrierDisplayName,
      policyNumber: state.policyNumber,
      recipientFullName: state.recipientFullName,
      proposedInsuredName: state.proposedInsuredName,
      riders: state.riders,
      selectedSources: state.selectedSources,
      pricingMode: state.pricingMode,
      fcraEnabled: state.fcraEnabled,
      selectedRiders: state.selectedRiders,
      fieldValues: state.fieldValues,
      pendingLetterType: state.pendingLetterType,
      open,
      close,
      toggleExpanded,
      toggleSource,
      reorderSources,
      setPricingMode,
      setFcraEnabled,
      toggleRider,
      setFieldValue,
      requestLetterType,
      confirmLetterTypeSwitch,
      cancelLetterTypeSwitch,
      isDirty,
      uploadFiles,
    }),
    [
      state,
      open,
      close,
      toggleExpanded,
      toggleSource,
      reorderSources,
      setPricingMode,
      setFcraEnabled,
      toggleRider,
      setFieldValue,
      requestLetterType,
      confirmLetterTypeSwitch,
      cancelLetterTypeSwitch,
      isDirty,
      uploadFiles,
    ],
  );
}
